import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Auth } from 'aws-amplify';

import { UserService } from '../../../core/api/user.service';
import { ErrorHelperService } from '../../../core/helper/error-helper.service';
import { RegexConstants } from '../../../shared/constants/regex.constants';
import { ToastLevel } from '../../../shared/models/toast-message.module';
import { ToastService } from '../../../shared/services/toast.service';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { AbstractAuthComponent } from '../abstract-auth.component';

@Component({
  selector: 'fc-auth-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['../auth.component.scss'],
})
export class ForgotPasswordComponent extends AbstractAuthComponent implements OnInit {
  forgotPasswordForm: FormGroup = new FormGroup({
    email: new FormControl(this.email, [Validators.required, Validators.pattern(RegexConstants.EMAIL)]),
  });
  forgotEmailErrorMessage?: string = undefined;

  destroyRef = inject(DestroyRef);

  constructor(
    readonly toastService: ToastService,
    readonly errorHelperService: ErrorHelperService,
    readonly spinnerService: SpinnerService,
    readonly userService: UserService
  ) {
    super();
    this.forgotPasswordForm.controls.email.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => {
        this.email = value;
        this.forgotEmailErrorMessage = this.errorHelperService.getEmailErrorMessage(
          this.forgotPasswordForm.controls.email
        );
      },
    });
    this.authModeChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.forgotPasswordForm.controls.email.setValue(this.email, {
          emitEvent: false,
        });
      },
    });
  }

  ngOnInit() {
    if (this.email) {
      this.forgotPasswordForm.controls.email.setValue(this.email, {
        emitEvent: false,
      });
    }
  }

  cancelForgotPassword() {
    this.authMode = this.authModeEnum.login;
  }

  sendResetEmail() {
    const email = this.forgotPasswordForm.controls.email.value;
    if (this.forgotPasswordForm.valid && email) {
      this.spinnerService.show('auth-spinner');
      this.userService.allowReset(email).subscribe({
        next: (resetAllowed: boolean) => {
          (resetAllowed ? Auth.forgotPassword(email) : Promise.resolve())
            .then((_cognitoResponse: string) => {
              const messageHeader = 'Your Reset Link Has Been Sent';
              const message = 'Please check your email to reset your password';
              this.toastService.sendMessage(message, ToastLevel.SUCCESS, 5000, messageHeader);
              if (!resetAllowed) {
                this.authMode = this.authModeEnum.login;
              } else {
                this.authMode = this.authModeEnum.resetPassword;
              }
            })
            .catch(_err => {
              if (_err.name === 'LimitExceededException') {
                this.errorMessage =
                  'Password reset attempts have exceeded limit; please try again after some time has passed.';
              } else {
                this.errorMessage = _err.message;
              }
              this.cognitoError = true;
            })
            .finally(() => {
              this.spinnerService.hide('auth-spinner');
            });
        },
      });
    }
  }
}
